import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  selectGenericFiles,
  selectResourceFiles,
  fetchGenericFiles,
  fetchResourceFiles,
  fetchExports,
  selectExports,
  selectPendingExports
} from '../../../../../slices/dataSlice';
import {selectResources, fetchResourceParticipants, selectParticipants} from '../../../../../slices/userSlice';
import {selectSelectedResource} from '../../../../../slices/preferencesSlice';
import {
  fetchAvailableHorizons,
  selectAvailableHorizons,
  selectAvailableHorizonsLoading
} from '../../../../../slices/marketSlice';
import {normalizeFile, normalizeExportsList} from '../utils/fileUtils';

/**
 * Custom hook to manage files data fetching and filtering
 */
export const useFilesData = (scope, selectedResource, search, showTargetUserSelect) => {
  const dispatch = useDispatch();
  const genericFiles = useSelector(selectGenericFiles);
  const resourceFiles = useSelector(selectResourceFiles);
  const exportedFiles = useSelector(selectExports);
  const pendingServerExports = useSelector(selectPendingExports);
  const availableResources = useSelector(selectResources);
  const globalSelectedResource = useSelector(selectSelectedResource);
  const participants = useSelector(selectParticipants);
  const availableHorizons = useSelector(selectAvailableHorizons);
  const availableHorizonsLoading = useSelector(selectAvailableHorizonsLoading);

  const [filtered, setFiltered] = useState([]);

  // Normalize exports list
  const exportsList = useMemo(() => normalizeExportsList(exportedFiles), [exportedFiles]);

  // Initial data fetching
  useEffect(() => {
    dispatch(fetchGenericFiles());
    dispatch(fetchExports());
  }, [dispatch]);

  // Fetch resource files whenever a resource is selected (regardless of the
  // active scope) so the Resource count badge is accurate even while viewing
  // Platform files.
  useEffect(() => {
    if (selectedResource) {
      dispatch(fetchResourceFiles({resourceId: selectedResource}));
    }
  }, [dispatch, selectedResource]);

  // Fetch participants when selected resource changes
  useEffect(() => {
    if (showTargetUserSelect && selectedResource) {
      dispatch(fetchResourceParticipants(selectedResource));
    }
  }, [dispatch, showTargetUserSelect, selectedResource]);

  // Fetch available horizons for the selected resource (drives the export
  // horizon dropdown). Reuses the existing marketSlice thunk/selectors.
  useEffect(() => {
    if (selectedResource) {
      dispatch(fetchAvailableHorizons({resourceId: selectedResource}));
    }
  }, [dispatch, selectedResource]);

  // Build and filter file list
  useEffect(() => {
    const q = (search || '').trim().toLowerCase();
    let source = [];

    if (scope === 'all') source = genericFiles || [];
    else if (scope === 'resource') source = resourceFiles || [];
    else source = [...(genericFiles || []), ...(resourceFiles || [])];

    const built = (source || []).map(normalizeFile);

    const result = built
      .filter(it => !q || String(it.name).toLowerCase().includes(q))
      .filter(it => !(scope === 'resource' && selectedResource) || String(it.resourceId) === String(selectedResource));

    setFiltered(result);
  }, [search, scope, selectedResource, genericFiles, resourceFiles]);

  // Per-scope file counts for the header switcher badges. The resource count
  // respects the selected resource, mirroring the filtering above.
  const scopeCounts = useMemo(() => ({
    all: (genericFiles || []).length,
    resource: (resourceFiles || [])
      .map(normalizeFile)
      .filter(it => !selectedResource || String(it.resourceId) === String(selectedResource))
      .length,
  }), [genericFiles, resourceFiles, selectedResource]);

  // Sort exports by date
  const sortedExports = useMemo(() => {
    const hasServerExports = exportsList && exportsList.length > 0;
    const displaySource = hasServerExports ? exportsList : [];

    return Array.isArray(displaySource)
      ? [...displaySource].sort((a, b) =>
          new Date(b.created_at || b.created || 0).getTime() -
          new Date(a.created_at || a.created || 0).getTime()
        )
      : [];
  }, [exportsList]);

  return {
    filtered,
    scopeCounts,
    sortedExports,
    availableResources,
    globalSelectedResource,
    participants,
    pendingServerExports,
    availableHorizons,
    availableHorizonsLoading
  };
};

